import { IServiceClient } from './Interfaces';
import {
    DeviceInfo,
    DocumentInfo,
    PageInfo,
    ParameterInfo,
    PageAction,
    EmailInfo,
    SearchInfo,
    SearchResult,
    RenderingExtension,
    RequestError
} from './Models';
import { ConfigurationInfo } from './Types/ConfigurationInfoType';

function getHeaders(authorizationToken: string, isPost: boolean = false, isFormUrlEncodedData: boolean = false): Record<string, any> {
    let headers: any = {
        'Accept': 'application/json, text/javascript, */*; q=0.01'
    };

    if (isPost) {
        if (isFormUrlEncodedData) {
            headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
        }
        else {
            headers['Content-Type'] = 'application/json; charset=UTF-8';
        }
    }

    if (authorizationToken) {
        headers['authorization'] = 'Bearer ' + authorizationToken;
    }

    return headers;
}

async function handleResponse(response: Response) {
    if (!response.ok) {
        let responseText: string = await response.text();
        let error = new RequestError(responseText, response.statusText);

        return Promise.reject(error);
    }

    if (response.status == 204) {
        return Promise.resolve();
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
        return response.json();
    }

    return response.text();
}

function sendPost(url: string, data: any = {}, authorizationToken: string = '', formUrlEncodedData: boolean = false): Promise<any> {
    return fetch(url, {
        method: 'POST',
        headers: getHeaders(authorizationToken, true, formUrlEncodedData),
        body: formUrlEncodedData ? data : JSON.stringify(data)
    })
        .then(handleResponse);
}

function sendGet(url: string, authorizationToken: string): Promise<any> {
    return fetch(url, {
        headers: getHeaders(authorizationToken)
    })
        .then(handleResponse);
}

function sendDelete(url: string, authorizationToken: string): Promise<any> {
    return fetch(url, {
        method: 'DELETE',
        headers: getHeaders(authorizationToken)
    })
        .then(handleResponse);
}

export class ServiceClientLoginInfo {
    url: string;
    username: string;
    password: string;

    constructor(url: string, username: string, password: string) {
        this.url = url;
        this.username = username;
        this.password = password;
    }
}

export class ServiceClientOptions {
    serviceUrl: string = '';
    loginInfo: ServiceClientLoginInfo | null;

    constructor(serviceUrl: string, loginInfo: ServiceClientLoginInfo | null = null) {
        this.serviceUrl = serviceUrl.replace(/\/+$/, '');
        this.loginInfo = loginInfo;
    }
}

export class ServiceClient implements IServiceClient {
    private loginPromise?: Promise<string>;

    private options: ServiceClientOptions;

    constructor(options: ServiceClientOptions) {
        this.options = options;
    }

    private validateClientID(clientId: string) {
        if (!clientId) {
            throw 'Invalid ClientId';
        }
    }

    private authenticatedGet(url: string): Promise<any> {
        return this.login().then(function(authorizationToken: string) {
            return sendGet(url, authorizationToken);
        });
    }

    private authenticatedPost(url: string, data: object): Promise<any> {
        return this.login().then(function(authorizationToken: string) {
            return sendPost(url, data, authorizationToken);
        });
    }

    private authenticatedDelete(url: string): Promise<any> {
        return this.login().then(function(authorizationToken: string) {
            return sendDelete(url, authorizationToken);
        });
    }

    login(): Promise<string> {
        if (!this.loginPromise) {
            this.loginPromise = this.createLoginPromise();
        }

        return this.loginPromise;
    }

    private createLoginPromise(): Promise<string> {
        let loginInfo = this.options.loginInfo;

        if (loginInfo && loginInfo.url && (loginInfo.username || loginInfo.password)) {
            let user = encodeURIComponent(loginInfo?.username || '');
            let pass = encodeURIComponent(loginInfo?.password || '');
            let data = `grant_type=password&username=${user}&password=${pass}`;

            return sendPost(loginInfo.url, data, '', true).then((result: any) => result.access_token);
        }

        return Promise.resolve('');
    }

    get(url: string): Promise<any> {
        return fetch(url).then((response: any) => response.text());
    }

    setAccessToken(accessToken: string) {
        this.loginPromise = Promise.resolve(accessToken);
    }

    registerClient(): Promise<string> {
        return this.authenticatedPost(`${this.options.serviceUrl}/clients`, {
            timeStamp: Date.now()
        }).then((result: any) => {
            return result.clientId;
        });
    }

    unregisterClient(clientId: string): Promise<any> {
        this.validateClientID(clientId);

        return this.authenticatedDelete(`${this.options.serviceUrl}/clients/${clientId}`);
    }

    getParameters(clientId: string, report: string, parameterValues?: any): Promise<ParameterInfo[]> {
        this.validateClientID(clientId);

        return this.authenticatedPost(`${this.options.serviceUrl}/clients/${clientId}/parameters`, {
            report: report,
            parameterValues: parameterValues || {}
        }).then((result: any) => result as ParameterInfo[]);
    }

    createReportInstance(clientId: string, report: string, parameterValues?: any): Promise<string> {
        this.validateClientID(clientId);

        return this.authenticatedPost(`${this.options.serviceUrl}/clients/${clientId}/instances`, {
            report: report,
            parameterValues: parameterValues
        }).then((result: any) => result.instanceId);
    }

    deleteReportInstance(clientId: string, instanceId: string): Promise<any> {
        this.validateClientID(clientId);

        return this.authenticatedDelete(`${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}`);
    }

    // eslint-disable-next-line max-params
    createReportDocument(
        clientId: string,
        instanceId: string,
        format: string,
        deviceInfo: DeviceInfo,
        useCache: boolean,
        baseDocumentID: string,
        actionID: string
    ): Promise<string> {
        this.validateClientID(clientId);

        deviceInfo.BasePath = this.options.serviceUrl;

        return this.authenticatedPost(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents`,
            {
                format: format,
                deviceInfo: deviceInfo,
                useCache: useCache,
                baseDocumentID: baseDocumentID,
                actionID: actionID
            }
        ).then((result: any) => result.documentId);
    }

    sendDocument(
        clientId: string,
        instanceId: string,
        documentId: string,
        mailArgs: EmailInfo
    ): Promise<any> {
        this.validateClientID(clientId);

        return this.authenticatedPost(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/send`,
            mailArgs
        );
    }

    deleteReportDocument(clientId: string, instanceId: string, documentId: string): Promise<any> {
        this.validateClientID(clientId);

        return this.authenticatedDelete(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}`
        );
    }

    getDocumentUrl(clientId: string, instanceId: string, documentId: string): string {
        return `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}`;
    }

    getDocumentInfo(clientId: string, instanceId: string, documentId: string): Promise<DocumentInfo> {
        this.validateClientID(clientId);

        return this.authenticatedGet(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/info`
        ).then((result: any) => result as DocumentInfo);
    }

    getPage(clientId: string, instanceId: string, documentId: string, pageNumber: number): Promise<PageInfo> {
        this.validateClientID(clientId);

        return this.authenticatedGet(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/pages/${pageNumber}`
        ).then((result: any) => {
            let pageInfo = result as PageInfo;
            pageInfo.pageActions = JSON.parse(result.pageActions) as PageAction[];
            return pageInfo;
        });
    }

    getDocumentFormats(): Promise<RenderingExtension[]> {
        return this.authenticatedGet(`${this.options.serviceUrl}/formats`).then(
            (result: any) => (result as RenderingExtension[]) || []
        );
    }

    getServiceVersion(): Promise<string> {
        return this.authenticatedGet(`${this.options.serviceUrl}/version`).then((result: any) =>
            result.toString()
        );
    }

    getServiceConfiguration(): Promise<ConfigurationInfo> {
        return this.authenticatedGet(`${this.options.serviceUrl}/configuration`).then((result: ConfigurationInfo) => result);
    }

    getResource(clientId: string, instanceId: string, documentId: string, resourceId: string): Promise<any> {
        this.validateClientID(clientId);

        return this.authenticatedGet(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/resources/${resourceId}`
        );
    }

    getSearchResults(
        clientId: string,
        instanceId: string,
        documentId: string,
        searchInfo: SearchInfo
    ): Promise<SearchResult[]> {
        this.validateClientID(clientId);

        return this.authenticatedPost(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/search`,
            searchInfo
        ).then(function(result: any) {
            return (result as SearchResult[]) || [];
        });
    }

    createAIThread(
        clientId: string,
        instanceId: string,
        report: string,
        parameterValues?: any
    ): Promise<string> {
        this.validateClientID(clientId);

        return this.authenticatedPost(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/ai`,{
                report: report,
                parameterValues: parameterValues
            }).then((result: any) => result);
    }

    getAIResponse(
        clientId: string,
        instanceId: string,
        documentId: string,
        threadId: string,
        query: string
    ): Promise<string> {
        this.validateClientID(clientId);

        return this.authenticatedPost(
            `${this.options.serviceUrl}/clients/${clientId}/instances/${instanceId}/documents/${documentId}/ai/${threadId}/query`,
            { query }
        ).then(function(result: any) {
            return (result as string) || '';
        });
    }

    getServiceUrl(): string {
        return this.options.serviceUrl;
    }

    keepClientAlive(clientId: string): Promise<any> {
        return this.authenticatedPost(`${this.options.serviceUrl}/clients/keepAlive/${clientId}`, {});
    }

    getClientsSessionTimeoutSeconds(): Promise<number> {
        return this.authenticatedGet(`${this.options.serviceUrl}/clients/sessionTimeout`).then(function(
            result: any
        ) {
            return result.clientSessionTimeout;
        });
    }
}
